<script module lang="ts">
  import type {
    OrgBillingUsage,
    OrganizationFrame,
    OrganizationUser,
  } from "@kontextwerk/control-web-client"
  import type { PermissionState } from "@kontextwerk/control-web-frame/logic/permissions/model"
  import type { KehrblattPlanCapabilities } from "./_shared/_logic/org-billing-plan-capabilities"
  import type { OrgaViewFrameContext } from "./context"
  import { provideOrganizationNavigationMetadata } from "./context"
  export type { OrgaViewFrameContext } from "./context"
</script>

<script lang="ts">
  import { onDestroy, onMount, untrack } from "svelte"
  import PageTemplate from "@kontextwerk/web-sdk/ui/layout/PageTemplate.svelte"
  import { formatChangeSuccessNotification } from "@kontextwerk/control-web-frame/logic/changeNotifications"
  import type { NotificationOpenCounts } from "@kontextwerk/control-web-client"
  import { OrgaViewFrameWrapper, SetWidgetContext } from "@kontextwerk/control-web-frame"
  import {
    buildAdministration,
    AdministrationPage,
    type AdministrationContentBindings,
    type AdministrationSelection,
  } from "@kontextwerk/control-web-frame/components/administration"
  import { AgentsSubview } from "@kontextwerk/control-web-frame/routes/OrgaView/pages/Administration/subviews/Agents"
  import { BillingSubview } from "@kontextwerk/control-web-frame/routes/OrgaView/pages/Administration/subviews/Billing"
  import {
    AiRulesSubview,
    createOrganizationAiRulesContext,
  } from "@kontextwerk/control-web-frame/components/sessionConfig/subviews/AiRules"
  import {
    PhoneNumbersSubview,
    createOrganizationPhoneNumbersContext,
  } from "@kontextwerk/control-web-frame/components/phone/subviews/PhoneNumbers"
  import { ContactProfilesSubview } from "@kontextwerk/control-web-frame/routes/OrgaView/pages/Administration/subviews/ContactProfiles"
  import { LegalDocumentsSubview } from "@kontextwerk/control-web-frame/routes/OrgaView/pages/Administration/subviews/LegalDocuments"
  import { buildOrganizationPortalItems } from "@kontextwerk/control-web-frame/routes/OrgaView/logic/organizationPortals"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { spaNavigate } from "../../_shared/_logic/navigation"
  import { clearAdminSession } from "./_shared/_logic/admin-session"
  import { resetSignupState, signupState } from "./_shared/_logic/signup"
  import { location } from "../../_shared/_logic/store"
  import { clearControlAuthenticatedBrowserState } from "@kontextwerk/control-web-frame/stores/browserState"
  import OverviewPage from "./pages/Overview"
  import { learningNavigationItem } from "./subviews/Learning/config"
  import WorkOrdersPage from "./pages/WorkOrders"
  import { OrganizationSubview } from "./pages/Administration/subviews/Organization"
  import { UsersSubview } from "./pages/Administration/subviews/Users"
  import { OrderTypesSubview } from "./pages/Administration/subviews/OrderTypes"
  import { AuditLogSubview } from "./pages/Administration/subviews/AuditLog"
  import { DistrictSubview } from "./pages/Administration/subviews/District"
  import { CustomersSubview } from "./pages/Administration/subviews/Customers"
  import { PropertiesSubview } from "./pages/Administration/subviews/Properties"
  import { DailyBriefingSubview } from "./pages/Administration/subviews/DailyBriefing"
  import { PlanningSubview } from "./pages/Administration/subviews/Planning"
  import { AnnouncementTemplatesSubview } from "./pages/Administration/subviews/AnnouncementTemplates"
  import { administrationConfig } from "./pages/Administration/config"
  import { withDistrictNavigation } from "./_logic/districtNavigation"
  import { getLegacyAdministrationRoutes, legacyAdministrationAliases } from "./_logic/legacy"
  import FeedbackPage from "./pages/Feedback"
  import NotificationsPage from "./pages/Notifications"
  import {
    ensureOrgBillingPlan,
    orgPlanCapabilities,
    orgPlanLookup,
  } from "./_shared/_logic/org-billing-plan"
  import { getAdminApi } from "../../api/kehrblatt/client"
  import { buildAppPermissionState } from "../_shared/_logic/permissions-model"
  import { hasActiveBillingSubscription } from "@kontextwerk/control-web-frame/routes/OrgaView/pages/Administration/subviews/Billing"
  import { buildNotificationsNavigationTitle } from "@kontextwerk/control-web-frame/routes/OrgaView/logic/notificationAttention"
  import { frameConfig as sharedFrameConfig } from "@kontextwerk/control-web-frame"
  import {
    billingFeaturesEnabled,
    normalizeBillingMode,
  } from "@kontextwerk/control-web-frame/logic/billingMode"
  import { buildOrgaViewAutomationPlan } from "./_logic/automation"
  import { buildTerminportalQuickLink } from "./_logic/adminQuickLinks"
  import {
    buildOrgaViewNavigation,
    buildOrgaViewPaths,
    buildFeedbackPage,
    getWorkOrdersTabs,
  } from "./navigation"
  import { getKehrblattControlAuthContext } from "../_shared/_logic/auth-context"
  import CustomerImportStatusOrganizationScope from "./_modules/customer-import-status-organization-scope"
  import LearningReminders from "./_modules/learning-reminders"
  import {
    createLearningProgressController,
    provideLearningProgress,
  } from "./_shared/_logic/learning-progress"
  import {
    isBillingStatusEnded,
    isFreeTrialExperienceOrganization,
    isFreeTrialRestrictionActive,
    organizationRequiresCheckout,
    resolveOrganizationSubscriptionRestriction,
  } from "./_shared/_logic/free-trial-experience"
  import { filterActiveOrganizationUsers } from "./_shared/_logic/organization-users"
  import { loadKehrblattOrganizationFrame } from "../../api/kehrblatt/organization-frame"

  const uuidPattern =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

  const asUuid = (value: string | null | undefined) => {
    const normalized = value ?? ""

    return uuidPattern.test(normalized) ? normalized : null
  }

  const {
    organizationId,
    organizationSlug = null,
    organizationResolvedId = null,
  } = $props<{
    organizationId: string
    organizationSlug?: string | null
    organizationResolvedId?: string | null
  }>()

  let frameConfig = $state<any>(null)
  const authContext = getKehrblattControlAuthContext()
  const emptyPermissions = buildAppPermissionState(null)
  let feedbackRefreshKey = $state(0)
  let openCounts = $state<NotificationOpenCounts | null>(null)
  let openCountsLoading = $state(false)
  let openCountsGeneration = 0
  let signupOrganizationRefreshKey: string | null = null
  let organizationRefreshPromise: Promise<OrganizationFrame | null> | null = null

  const organization = $derived(frameConfig?.organization ?? null)
  const loading = $derived(frameConfig?.loading ?? false)
  const error = $derived(frameConfig?.error ?? null)
  const normalizedPath = $derived(frameConfig?.normalizedPath ?? "/")
  const effectiveOrganizationSlug = $derived(
    organization?.slug ?? organizationSlug ?? frameConfig?.pathOrgSegment ?? null,
  )
  const terminportalQuickLink = $derived(buildTerminportalQuickLink(effectiveOrganizationSlug))
  const systemAdminExtraLinks = $derived(terminportalQuickLink ? [terminportalQuickLink] : [])
  const apiOrganizationId = $derived(
    asUuid(frameConfig?.organization?.id) ??
      asUuid(frameConfig?.resolvedOrganizationId) ??
      asUuid(frameConfig?.organizationId) ??
      asUuid(organizationId),
  )
  const learningScope = $derived(
    apiOrganizationId && $authContext?.currentUserId
      ? { organizationId: apiOrganizationId, userId: $authContext.currentUserId }
      : null,
  )
  const learningProgress = provideLearningProgress(
    createLearningProgressController(() => learningScope),
  )
  $effect(() => {
    const scope = learningScope
    untrack(() => learningProgress.activate(scope))
  })
  onMount(() => {
    const refreshLearning = () => void learningProgress.loadCourse()
    window.addEventListener("focus", refreshLearning)

    return () => window.removeEventListener("focus", refreshLearning)
  })
  onDestroy(() => learningProgress.dispose())
  const tabPaths = $derived.by(() => buildOrgaViewPaths(frameConfig?.tabPath))
  const organizationBasePath = $derived(frameConfig?.basePath ?? "")

  const notificationsNavTitle = $derived(buildNotificationsNavigationTitle(openCounts))

  const permissions = $derived($authContext?.permissions ?? emptyPermissions)
  const includeAllUsersForNotifications = $derived(
    permissions.allows("core.notifications", "view", { scope: "org" }),
  )
  const canViewNotifications = $derived(
    permissions.allows("core.notifications", "view", { scope: "user" }) ||
      permissions.allows("core.notifications", "view", { scope: "org" }) ||
      permissions.allows("core.notifications", "manage", { scope: "user" }) ||
      permissions.allows("core.notifications", "manage", { scope: "org" }),
  )
  const canViewBilling = $derived(
    permissions.allows("core.billing", "view") || permissions.allows("core.billing", "manage"),
  )
  const billingMode = $derived(normalizeBillingMode($sharedFrameConfig?.billing?.mode, "none"))
  const billingAvailable = $derived(billingFeaturesEnabled(billingMode, "none"))
  const billingEnabled = $derived(billingAvailable)
  const isAdmin = $derived(permissions.isSuper)
  const currentUserRole = $derived($authContext?.currentUserRole ?? null)
  const currentUserId = $derived($authContext?.currentUserId ?? null)
  const currentUserOrganizationId = $derived($authContext?.currentUserOrganizationId ?? null)
  const loadOrganizationWithSettings = (
    identifier: string,
    options?: { initialOrganization?: OrganizationFrame | null },
  ): Promise<OrganizationFrame> =>
    loadKehrblattOrganizationFrame(identifier, {
      initialOrganization: options?.initialOrganization,
      canViewOrganizationUsers:
        permissions.isSuper ||
        permissions.allows("core.users", "view") ||
        permissions.allows("core.users", "manage"),
      currentUserId,
    })
  const signupBillingStatus = $derived.by(() => {
    const signupOrgId = asUuid($signupState.organizationId)

    if (!signupOrgId || !apiOrganizationId || signupOrgId !== apiOrganizationId) return null

    return $signupState.status ?? null
  })
  const orgBillingPlanState = $derived($orgPlanLookup(organization?.id ?? apiOrganizationId ?? ""))
  const orgBilling = $derived(orgBillingPlanState?.billing ?? null)
  const orgBillingResolved = $derived(
    orgBillingPlanState?.loadedAt !== null &&
      orgBillingPlanState?.loadedAt !== undefined &&
      !orgBillingPlanState?.error,
  )
  const effectiveBillingStatus = $derived(signupBillingStatus ?? orgBilling?.status ?? null)
  const organizationCheckoutRequired = $derived(organizationRequiresCheckout(organization))
  const isFreeTrialExperience = $derived(isFreeTrialExperienceOrganization(organization))
  const trialRestrictionsActive = $derived(
    isFreeTrialRestrictionActive(organization, permissions.isSuper),
  )
  const billingRequiresCheckout = $derived.by(() => {
    if (!billingAvailable || !billingEnabled || !canViewBilling) return false

    if (!organizationCheckoutRequired) return false

    if (isBillingStatusEnded(effectiveBillingStatus)) return false

    const hasBillingStatus = signupBillingStatus !== null || orgBillingResolved

    if (!hasBillingStatus) return false

    return !hasActiveBillingSubscription(effectiveBillingStatus)
  })
  const subscriptionRestriction = $derived(
    resolveOrganizationSubscriptionRestriction({
      billingAvailable,
      organization,
      signupStatus: signupBillingStatus,
      billing: orgBilling,
    }),
  )
  const subscriptionRestrictionActive = $derived(subscriptionRestriction.active)
  const restrictNavigation = $derived(false)
  const tariffCapabilities = $derived(
    $orgPlanCapabilities(organization?.id ?? apiOrganizationId ?? ""),
  )
  const voiceAllowed = $derived(tariffCapabilities.hasPhone)
  const navigationMetadataReady = $derived(
    Boolean(organization) &&
      !loading &&
      (!billingAvailable || (orgBillingResolved && !orgBillingPlanState?.loading)),
  )

  const resolveRefreshOrganizationTarget = () =>
    apiOrganizationId ?? effectiveOrganizationSlug ?? organizationSlug ?? organizationId ?? null

  const refreshOrganization = async (): Promise<OrganizationFrame | null> => {
    const target = resolveRefreshOrganizationTarget()

    if (!target) return organization

    if (organizationRefreshPromise) return organizationRefreshPromise

    organizationRefreshPromise = loadOrganizationWithSettings(target)
      .then((nextOrganization) => {
        frameConfig?.setOrganization?.(nextOrganization)

        return nextOrganization
      })
      .finally(() => {
        organizationRefreshPromise = null
      })

    return organizationRefreshPromise
  }

  const reloadOrganization = () => {
    void refreshOrganization().catch(() => {
      console.error("organization_frame_refresh_failed")
    })
  }

  const handleUsersUpdated = async (users: OrganizationUser[]) => {
    if (!organization || !frameConfig) return

    frameConfig.setOrganization({ ...organization, users })
    try {
      await refreshOrganization()
    } catch {
      console.error("organization_frame_users_refresh_failed")
    }
  }

  const loadOpenCounts = async (id: string, includeAllUsers: boolean) => {
    const request = ++openCountsGeneration
    openCountsLoading = true
    try {
      const result = await getAdminApi().notifications.openCounts(id, { includeAllUsers })

      if (request !== openCountsGeneration || id !== apiOrganizationId) return

      openCounts = result
    } catch (err) {
      if (request === openCountsGeneration) console.error("open_counts_load_failed", err)
    } finally {
      if (request === openCountsGeneration) openCountsLoading = false
    }
  }

  const refreshOpenCounts = () => {
    if (apiOrganizationId && canViewNotifications) {
      void loadOpenCounts(apiOrganizationId, includeAllUsersForNotifications)
    }
  }

  const handleFeedbackCreated = () => {
    feedbackRefreshKey++
  }

  const handleNotificationsChanged = () => {
    refreshOpenCounts()
  }

  const handleDeleteOrganization = async () => {
    if (!organization) return

    await getAdminApi().organization.registry.delete(organization.id)

    if (
      currentUserRole !== "admin" &&
      currentUserOrganizationId &&
      currentUserOrganizationId === organization.id
    ) {
      clearControlAuthenticatedBrowserState()
      clearAdminSession()
      resetSignupState()
      newNotification({
        class: "success",
        html: `${formatChangeSuccessNotification(`Löschung von Organisation ${organization.name} gestartet.`)}<br/>Sie wurden ausgeloggt.`,
      })
      spaNavigate("/login", { replace: true, noSearchParam: false })

      return
    }

    newNotification({
      class: "success",
      html: formatChangeSuccessNotification(
        `Löschung von Organisation ${organization.name} gestartet.`,
      ),
    })
    spaNavigate("/admin/organizations", { replace: true, noSearchParam: false })
  }

  const ensureBillingPlanCapabilities = (options?: { maxAgeMs?: number }) => {
    if (!apiOrganizationId || !billingAvailable) return

    void ensureOrgBillingPlan(apiOrganizationId, options)
  }

  onMount(() => {
    const handleFocus = () => {
      ensureBillingPlanCapabilities({ maxAgeMs: 60_000 })
    }
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        ensureBillingPlanCapabilities({ maxAgeMs: 60_000 })
      }
    }

    window.addEventListener("focus", handleFocus)
    document.addEventListener("visibilitychange", handleVisibility)

    return () => {
      window.removeEventListener("focus", handleFocus)
      document.removeEventListener("visibilitychange", handleVisibility)
    }
  })

  const viewFrameContext = $derived.by<OrgaViewFrameContext>(() => ({
    permissions,
    organization,
    organizationId: apiOrganizationId ?? null,
    loading,
    error,
    onRetry: reloadOrganization,
    refreshOrganization,
    onUsersUpdated: handleUsersUpdated,
    onNotificationsChanged: handleNotificationsChanged,
    refreshKey: feedbackRefreshKey,
    onFeedbackCreated: handleFeedbackCreated,
    billingMode,
    billingAvailable,
    billingEnabled,
    billingRequiresCheckout,
    orgBilling,
    billingStateLoading: orgBillingPlanState?.loading ?? false,
    billingStateError: orgBillingPlanState?.error ?? null,
    billingStateResolved: orgBillingResolved,
    isFreeTrialExperience,
    trialRestrictionsActive,
    hasHistoricalConsumption: false,
    usagePreview: null,
    planCardPresentation: $sharedFrameConfig?.billing?.planCardPresentation ?? null,
    subscriptionRestrictionActive,
    tariffCapabilities,
    voiceAllowed,
    currentUserId,
    currentUserRole,
    currentUserOrganizationId,
    onOrganizationUpdated: (event) => {
      if (event?.detail) {
        reloadOrganization()
      }
    },
    onDeleteOrganization: handleDeleteOrganization,
  }))

  const activeWorkOrdersTabId = $derived.by(() => {
    if (!tabPaths.workOrders || !normalizedPath.startsWith(tabPaths.workOrders)) return null

    const tabs = getWorkOrdersTabs(viewFrameContext)

    if (!tabs.length) return null

    const rawTab = new URLSearchParams(($location.search ?? "").replace(/^\?/, "")).get("tab")
    const parsedTab = rawTab === null ? 0 : Number.parseInt(rawTab, 10)
    const tabIndex = Number.isFinite(parsedTab) && parsedTab >= 0 ? parsedTab : 0

    return tabs[tabIndex]?.id ?? tabs[0]?.id ?? null
  })

  const assistSection = $derived.by(() => {
    const administrationPath = `${organizationBasePath}/administration`

    if (
      normalizedPath.startsWith(`${administrationPath}/district`) ||
      normalizedPath.startsWith(`${administrationPath}/customers`) ||
      normalizedPath.startsWith(`${administrationPath}/properties`)
    )
      return "CUSTOMERS"

    if (normalizedPath === `${administrationPath}/users`) return "USERS"

    if (
      normalizedPath.startsWith(`${administrationPath}/organization`) ||
      normalizedPath.startsWith(`${administrationPath}/ai-rules`) ||
      normalizedPath === `${administrationPath}/planning`
    )
      return "SETTINGS"

    if (
      normalizedPath === administrationPath ||
      normalizedPath.startsWith(`${administrationPath}/`)
    )
      return "MANAGEMENT"

    if (tabPaths.workOrders && normalizedPath.startsWith(tabPaths.workOrders)) {
      return activeWorkOrdersTabId === "calendar" ? "CALENDAR" : "WORK_ORDERS"
    }

    if (tabPaths.feedback && normalizedPath.startsWith(tabPaths.feedback)) return "FEEDBACK"

    if (tabPaths.notifications && normalizedPath.startsWith(tabPaths.notifications))
      return "NOTIFICATIONS"

    return "OVERVIEW"
  })

  const sourcePageNavigation = $derived.by(() => {
    if (!tabPaths.overview) return []

    return buildOrgaViewNavigation({
      context: viewFrameContext,
      paths: tabPaths,
      capabilities: {
        restrictNavigation,
        notificationsNavTitle,
      },
      snippets: {
        overview: overviewPageContent,
        workOrders: workOrdersPageContent,
        notifications: notificationsPageContent,
      },
    })
  })
  const administrationContext = $derived({
    ...viewFrameContext,
    userOrganizationId: viewFrameContext.currentUserOrganizationId,
    subscriptionExpired: subscriptionRestrictionActive,
  })
  const administrationBindings = $derived<AdministrationContentBindings>({
    organization: organizationContent,
    users: usersContent,
    "order-types": orderTypesContent,
    audit: auditLogContent,
    district: districtContent,
    customers: customersContent,
    properties: propertiesContent,
    planning: planningContent,
    "daily-briefing": dailyBriefingContent,
    "announcement-templates": announcementTemplatesContent,
    agents: agentsContent,
    "ai-rules": aiRulesContent,
    "phone-numbers": phoneNumbersContent,
    "contact-profiles": contactProfilesContent,
    billing: billingContent,
    "legal-documents": legalDocumentsContent,
  })
  const feedbackPage = $derived(
    buildFeedbackPage(viewFrameContext, tabPaths.feedback, feedbackPageContent),
  )
  const aiRulesContext = $derived(createOrganizationAiRulesContext(administrationContext))
  const phoneNumbersContext = $derived(createOrganizationPhoneNumbersContext(administrationContext))
  const administration = $derived(
    withDistrictNavigation(
      buildAdministration({
        host: {
          basePath: organizationBasePath,
          rootPath: `${organizationBasePath}/administration`,
          title: "Verwaltungsbereich",
          activePageId: "administration",
        },
        config: administrationConfig,
        context: viewFrameContext,
        bindings: administrationBindings,
        existingPages: [...sourcePageNavigation, ...(feedbackPage ? [feedbackPage] : [])],
        overviewContent: administrationOverviewContent,
        legacyRoutes: getLegacyAdministrationRoutes(viewFrameContext, $location),
        aliases: legacyAdministrationAliases,
      }),
    ),
  )
  const pageNavigation = $derived(
    !feedbackPage && $location.path === tabPaths.feedback
      ? [
          ...administration.pages,
          {
            id: "feedback-unavailable",
            title: "Nicht verfügbar",
            path: tabPaths.feedback,
            content: administrationUnavailableContent,
          },
        ]
      : administration.getPagesForLocation($location, administrationUnavailableContent),
  )
  provideOrganizationNavigationMetadata(() => ({
    basePath: organizationBasePath,
    ready: navigationMetadataReady,
    pages: administration.pages,
  }))
  const portalItems = $derived(
    buildOrganizationPortalItems({
      organizationSlug: effectiveOrganizationSlug,
      showWerkAdmin: tariffCapabilities.hasAiAgents,
      showIndexAdmin: tariffCapabilities.hasAiAgents,
      disabled: !organization,
      extraLinks: systemAdminExtraLinks,
    }),
  )
  const viewKey = $derived(`org:${organizationSlug?.trim() || organizationId}`)

  const defaultPath = $derived(administration.pages[0]?.path ?? null)
  const workOrdersNavigationPath = $derived(
    sourcePageNavigation.find((entry) => entry.path === tabPaths.workOrders)?.path ?? null,
  )

  const automationPlan = $derived.by(() =>
    buildOrgaViewAutomationPlan({
      defaultPath,
      apiOrganizationId,
      normalizedPath,
      canViewNotifications,
      billingAvailable,
      workOrdersPath: tabPaths.workOrders,
      workOrdersNavigationPath,
    }),
  )

  $effect(() => {
    const setDefaultPath = frameConfig?.setDefaultPath
    const path = defaultPath
    untrack(() => setDefaultPath?.(path))
  })

  $effect(() => {
    const id = apiOrganizationId
    const available = billingAvailable

    if (id && available) {
      // Provider cache reads are not additional dependencies of the frame.
      untrack(() => {
        void ensureOrgBillingPlan(id)
      })
    }
  })

  $effect(() => {
    const id = apiOrganizationId
    const allowed = canViewNotifications
    const includeAllUsers = includeAllUsersForNotifications
    openCounts = null
    openCountsLoading = false

    if (id && allowed) void loadOpenCounts(id, includeAllUsers)

    return () => {
      openCountsGeneration += 1
    }
  })

  $effect(() => {
    const alias = administration.resolveLegacyLocation($location)

    if (typeof alias === "string") {
      if (navigationMetadataReady) {
        untrack(() => spaNavigate(alias, { replace: true, noSearchParam: true }))
      }
    } else if (alias === undefined && automationPlan.navigate) {
      const navigate = automationPlan.navigate
      const notice = automationPlan.accessNotice
      untrack(() => {
        if (notice) newNotification({ class: "info", html: notice })

        spaNavigate(navigate.to, {
          replace: navigate.replace,
          noSearchParam: navigate.noSearchParam,
        })
      })
    }
  })

  // effect-ok: signup-store-sync - a successful checkout changes user provisioning outside this component.
  $effect(() => {
    const signupOrgId = asUuid($signupState.organizationId)

    if (!signupOrgId || !apiOrganizationId || signupOrgId !== apiOrganizationId) return

    if (!organization) return

    const status = String($signupState.status ?? "").toLowerCase()

    if (status !== "active" && status !== "trialing") return

    if (filterActiveOrganizationUsers(organization?.users ?? []).length > 0) return

    const refreshKey = `${signupOrgId}:${status}`

    if (signupOrganizationRefreshKey === refreshKey) return

    signupOrganizationRefreshKey = refreshKey
    reloadOrganization()
  })
</script>

{#snippet administrationOverviewContent()}
  <AdministrationPage sections={administration.sections} />
{/snippet}

{#snippet administrationUnavailableContent()}
  {#if loading}
    <p role="status">Verwaltungsbereich wird geladen …</p>
  {:else if error}
    <p role="alert">{error}</p>
  {:else}
    <p>Dieser Bereich ist für Ihre Organisation oder Ihre Berechtigungen nicht verfügbar.</p>
  {/if}
{/snippet}

{#snippet overviewPageContent(activeTab: string)}
  <OverviewPage {viewFrameContext} {activeTab} />
{/snippet}

{#snippet workOrdersPageContent(activeTab: string)}
  <WorkOrdersPage {viewFrameContext} {activeTab} />
{/snippet}

{#snippet notificationsPageContent(activeTab: string)}
  <NotificationsPage {viewFrameContext} {activeTab} />
{/snippet}

{#snippet feedbackPageContent()}<FeedbackPage {viewFrameContext} />{/snippet}
{#snippet organizationContent(selection: AdministrationSelection)}<OrganizationSubview
    {viewFrameContext}
    activeTab={selection.tabId ?? selection.pageId}
  />{/snippet}
{#snippet usersContent(_selection: AdministrationSelection)}<UsersSubview
    {viewFrameContext}
  />{/snippet}
{#snippet orderTypesContent(_selection: AdministrationSelection)}<OrderTypesSubview
    {viewFrameContext}
  />{/snippet}
{#snippet auditLogContent(_selection: AdministrationSelection)}<AuditLogSubview
    {viewFrameContext}
  />{/snippet}
{#snippet districtContent(_selection: AdministrationSelection)}<DistrictSubview
    {viewFrameContext}
  />{/snippet}
{#snippet customersContent(_selection: AdministrationSelection)}<CustomersSubview
    {viewFrameContext}
  />{/snippet}
{#snippet propertiesContent(_selection: AdministrationSelection)}<PropertiesSubview
    {viewFrameContext}
  />{/snippet}
{#snippet dailyBriefingContent(_selection: AdministrationSelection)}<DailyBriefingSubview
    {viewFrameContext}
  />{/snippet}
{#snippet planningContent(_selection: AdministrationSelection)}<PlanningSubview
    {viewFrameContext}
  />{/snippet}
{#snippet announcementTemplatesContent(
  selection: AdministrationSelection,
)}<AnnouncementTemplatesSubview
    {viewFrameContext}
    activeTab={selection.tabId ?? selection.pageId}
  />{/snippet}
{#snippet agentsContent(_selection: AdministrationSelection)}<AgentsSubview
    viewFrameContext={administrationContext}
  />{/snippet}
{#snippet billingContent(selection: AdministrationSelection)}<BillingSubview
    viewFrameContext={administrationContext}
    activePage={selection.pageId}
  />{/snippet}
{#snippet aiRulesContent(selection: AdministrationSelection)}<AiRulesSubview
    context={aiRulesContext}
    activePage={selection.pageId}
  />{/snippet}
{#snippet phoneNumbersContent(_selection: AdministrationSelection)}<PhoneNumbersSubview
    context={phoneNumbersContext}
    helpModalTextId="phoneNumbers"
  />{/snippet}
{#snippet contactProfilesContent(_selection: AdministrationSelection)}<ContactProfilesSubview
    viewFrameContext={administrationContext}
  />{/snippet}
{#snippet legalDocumentsContent(_selection: AdministrationSelection)}
  {#if organization}
    <LegalDocumentsSubview
      organizationId={organization.id}
      canManageDocuments={permissions.allows("core.billing", "manage")}
    />
  {/if}
{/snippet}

{#key apiOrganizationId ?? "none"}
  <CustomerImportStatusOrganizationScope organizationId={apiOrganizationId ?? null} />
{/key}

<OrgaViewFrameWrapper
  {organizationId}
  {organizationSlug}
  {organizationResolvedId}
  loadOrganization={loadOrganizationWithSettings}
  bind:config={frameConfig}
>
  {#snippet viewFrame(_config)}
    {#if learningScope && effectiveOrganizationSlug}
      <LearningReminders organization={effectiveOrganizationSlug} userId={learningScope.userId} />
    {/if}
    {#if frameConfig}
      <SetWidgetContext {...frameConfig.widgetContextBase} taskHint={assistSection} />
    {/if}

    <PageTemplate
      {viewKey}
      gobackLink={isAdmin ? "/admin" : ""}
      title={organization?.name ?? "Organisation"}
      {pageNavigation}
      workspaceNavigation={{
        items: sourcePageNavigation.map((page) => ({
          id: page.id ?? page.path,
          title: page.title,
          href: page.path,
          icon: page.icon,
        })),
        footerItems: [
          ...portalItems,
          ...(effectiveOrganizationSlug ? [learningNavigationItem(effectiveOrganizationSlug)] : []),
          ...(feedbackPage
            ? [
                {
                  id: "feedback",
                  title: feedbackPage.title,
                  href: feedbackPage.path,
                  icon: feedbackPage.icon,
                },
              ]
            : []),
          ...(administration.navigationItem ? [administration.navigationItem] : []),
        ],
      }}
      locationStore={location}
      testId="controlAdmin.organizationView"
    />
  {/snippet}
</OrgaViewFrameWrapper>
